/* eslint-disable react/style-prop-object */
/* eslint-disable no-nested-ternary */
import { PopoverPortal } from "@radix-ui/react-popover";
import type { FilterableIndicator } from "components/screener/v2/ScreenerFilterableIndicators";
import { FilterRuleDefinitionHeader } from "components/screener/v2/filters/FilterRuleDefinition";
import { VirtualOperatorKeyboard } from "components/screener/v2/filters/VirtualOperatorKeyboard";
import { evaluateFiltersExpression } from "components/screener/v2/filters/evaluateExpression";
import type { Expression } from "components/screener/v2/filters/generated/peggyParser.generated";
import type { FilterCondition, NumericFilterRuleInput } from "components/screener/v2/filters/types";
import { Input } from "components/ui/Input";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import { Separator } from "components/ui/Separator";
import classNames from "components/ui/classNames";
import debounce from "lodash/debounce";
import { CircleAlertIcon, CircleCheckIcon, EraserIcon } from "lucide-react";
import type { ReactNode } from "react";
import { startTransition, useCallback, useRef, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { IIndicatorKind } from "types/graphqlTypes";
import { useMediaQuery } from "usehooks-ts";

function operatorTokenToSafeLabel(operator: string): "GT" | "LT" | "EQ" | "NE" | "GTE" | "LTE" {
  if (operator === ">") return "GT";
  if (operator === "<") return "LT";
  if (operator === "=") return "EQ";
  if (operator === "!=") return "NE";
  if (operator === ">=") return "GTE";
  if (operator === "<=") return "LTE";
  throw new Error(`Invalid operator: ${operator}`);
}

type InternalFilterState = {
  expression: string;
  isValid: boolean;
  evaluationResult: ReturnType<typeof evaluateFiltersExpression> | null;
};

interface NumericFilterRuleDefinitionProps {
  indicator: FilterableIndicator;
  condition?: FilterCondition | null;
  children: ReactNode;
  onRemoveFilter: () => void;
  onChange: (input: NumericFilterRuleInput | null) => void;
}

export function NumericFilterRuleDefinition({
  indicator,
  condition,
  children,
  onRemoveFilter,
  onChange,
}: NumericFilterRuleDefinitionProps) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [filterState, setFilterState] = useState<InternalFilterState>(() => {
    if (condition?.expression) {
      try {
        const evaluationResult = evaluateFiltersExpression(condition.expression);
        return {
          expression: condition.expression,
          isValid: true,
          evaluationResult,
        };
      } catch {
        return {
          expression: "",
          isValid: false,
          evaluationResult: null,
        };
      }
    }
    return {
      expression: "",
      isValid: false,
      evaluationResult: null,
    };
  });

  const processConditionExpression = useCallback(
    debounce((expressionAsWritten: string) => {
      if (expressionAsWritten === "") {
        setFilterState({ expression: "", isValid: false, evaluationResult: null });
        return;
      }

      try {
        setFilterState({
          expression: expressionAsWritten,
          isValid: true,
          evaluationResult: evaluateFiltersExpression(expressionAsWritten),
        });
      } catch {
        setFilterState({ expression: expressionAsWritten, isValid: false, evaluationResult: null });
      }
    }, 100),
    [],
  );

  const confirmFilter = useCallback((expression: string, evaluationResult: Expression) => {
    startTransition(() => {
      setIsOpen(false);
      // setFilterState({ expression: "", isValid: false, evaluationResult: null });

      if (evaluationResult.type === "RANGE") {
        onChange({
          type: "NUMERIC",
          expression,
          condition: {
            type: "RANGE",
            minValue: evaluationResult.range[0],
            maxValue: evaluationResult.range[1],
          },
        });
      }
      if (evaluationResult.type === "COMPARISON") {
        onChange({
          type: "NUMERIC",
          expression,
          condition: {
            type: "COMPARISON",
            operator: operatorTokenToSafeLabel(evaluationResult.operator),
            value: evaluationResult.value,
          },
        });
      }
      if (evaluationResult.type === "VALUE") {
        onChange({
          type: "NUMERIC",
          expression,
          condition: {
            type: "COMPARISON",
            operator: operatorTokenToSafeLabel("="),
            value: evaluationResult.value,
          },
        });
      }
    });
  }, []);

  const pointRef = useRef({ x: 0, y: 0 });
  const virtualPopoverRef = useRef({
    getBoundingClientRect: () => DOMRect.fromRect({ width: 0, height: 0, ...pointRef.current }),
  });
  const isMobile = useMediaQuery("(max-width: 639px)");
  return (
    <Popover modal={true} open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
      {isOpen && (
        <PopoverPortal>
          <div className="absolute bottom-0 left-0 right-0 top-0 isolate z-10 bg-black/5 backdrop-blur-sm" />
        </PopoverPortal>
      )}
      {isOpen && isMobile ? (
        <PopoverAnchor virtualRef={virtualPopoverRef} />
      ) : (
        <PopoverTrigger asChild={true}>{children}</PopoverTrigger>
      )}
      <PopoverContent
        className={classNames(
          "relative flex flex-col gap-y-2 rounded-2xl border border-zinc-600/40 py-2 shadow-lg shadow-zinc-300",
          "dark:border-slate-700 dark:shadow-2xl dark:shadow-slate-900",
          {
            "w-80 max-w-xs": !isMobile,
            "m-4 w-[calc(100vw-theme('spacing.8'))]": isMobile,
          },
        )}
        side={isMobile ? "top" : "bottom"}
        align={isMobile ? "start" : "start"}
        sideOffset={isMobile ? 0 : 8}
      >
        <FilterRuleDefinitionHeader
          indicator={indicator}
          appliedCondition={condition}
          onOpenChange={setIsOpen}
          onRemoveFilter={() => {
            startTransition(() => {
              onRemoveFilter();
              setIsOpen(false);
            });
          }}
          onConfirmFilter={() => {
            if (filterState.evaluationResult) {
              confirmFilter(filterState.expression, filterState.evaluationResult);
              setIsOpen(false);
              return;
            }
            startTransition(() => {
              onRemoveFilter();
              setIsOpen(false);
            });
          }}
        />
        <Separator />

        <div className="flex items-center gap-x-2 px-2">
          <div className="relative w-full">
            <Input
              ref={inputRef}
              autoFocus={true}
              inputMode="decimal"
              onFocus={(e) => {
                const inputValue = e.currentTarget.value.toLowerCase().trim();
                startTransition(() => {
                  processConditionExpression(inputValue);
                });
              }}
              onChange={(e) => {
                const inputValue = e.currentTarget.value.toLowerCase().trim();
                startTransition(() => {
                  processConditionExpression(inputValue);
                });
              }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  if (filterState.evaluationResult) {
                    confirmFilter(filterState.expression, filterState.evaluationResult);
                  }
                }
              }}
              defaultValue={filterState.expression}
              placeholder={intl.formatMessage({ defaultMessage: "Digite o filtro", id: "tnrteb" })}
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              autoSave="off"
              maxLength={16}
              className={classNames(
                "font-mono tracking-widest placeholder:tracking-normal",
                "h-10 rounded-xl border-transparent bg-zinc-900/5 text-base placeholder:text-zinc-500",
                "font-bold placeholder:font-bold",
                "focus:border-transparent focus:bg-zinc-400/10 focus:ring-[1px] focus:ring-transparent",
                "focus:ring-zinc-300 focus:ring-offset-2 focus:ring-offset-white",
                "dark:bg-zinc-500/30 dark:placeholder:text-zinc-300 dark:focus:ring-1 dark:focus:ring-zinc-400/40 dark:focus:ring-offset-1 dark:focus:ring-offset-transparent",
                "md:text-sm",
                {
                  "text-viridian-600 focus:bg-viridian-50 focus:ring-viridian-600/30 dark:text-emerald-300 dark:focus:bg-emerald-800/30 dark:focus:ring-transparent":
                    filterState.isValid && filterState.expression !== "",
                  "text-red-600 placeholder:text-red-300 dark:text-red-300 dark:focus:bg-red-900/40 dark:focus:ring-red-400 dark:focus:ring-transparent":
                    !filterState.isValid && filterState.expression !== "",
                },
                "dark:focus-visible:outline-transparent",
              )}
            />

            {filterState.evaluationResult && (
              <CircleCheckIcon
                strokeWidth={2}
                absoluteStrokeWidth={true}
                className={classNames(
                  "absolute inset-y-1/2 right-4 -translate-y-1/2",
                  "fill-emerald-100 stroke-emerald-700",
                  "group-hover:fill-blue-100 group-hover:stroke-blue-600",
                  "group-aria-checked:fill-transparent",
                  "dark:fill-emerald-400/30 dark:stroke-emerald-300",
                  "size-4",
                )}
              />
            )}

            {filterState.expression !== "" && !filterState.isValid && (
              <CircleAlertIcon
                strokeWidth={2}
                absoluteStrokeWidth={true}
                className={classNames(
                  "absolute inset-y-1/2 right-4 -translate-y-1/2",
                  "fill-red-100 stroke-red-700",
                  "group-hover:fill-blue-100 group-hover:stroke-blue-600",
                  "group-aria-checked:fill-transparent",
                  "dark:fill-red-400/30 dark:stroke-red-300",
                  "size-4",
                )}
              />
            )}
          </div>
          <button
            type="button"
            disabled={filterState.expression === ""}
            title={intl.formatMessage({ defaultMessage: "Limpar", id: "RfcCN4" })}
            className={classNames(
              "ml-auto flex size-10 items-center justify-center rounded-full p-1 outline-none",
              "bg-stone-50 hover:bg-stone-200/80",
              "dark:bg-stone-600/20",
              "dark:hover:bg-stone-600/40",
              "group",
              "disabled:pointer-events-none disabled:opacity-50",
              "disabled:bg-slate-100",
            )}
            onClick={() => {
              startTransition(() => {
                if (inputRef.current) {
                  inputRef.current.value = "";
                  inputRef.current.focus();
                }
              });
            }}
          >
            <EraserIcon
              absoluteStrokeWidth={true}
              strokeWidth={1.75}
              className={classNames(
                "stroke-stone-600",
                "group-hover:stroke-stone-600 group-disabled:stroke-slate-400",
                "dark:stroke-stone-500 dark:group-hover:stroke-stone-500",
                "size-6",
              )}
            />
          </button>
        </div>

        <div className="px-1">
          <VirtualOperatorKeyboard inputRef={inputRef} />
        </div>

        {filterState.evaluationResult && (
          <div className="-mb-1 flex flex-col items-center justify-center rounded-2xl bg-zinc-50 p-4 py-3 dark:bg-slate-900">
            {filterState.evaluationResult.type === "RANGE" && (
              <p className="flex font-bold text-indigo-600 dark:text-lime-400">
                <FormattedMessage defaultMessage="Entre" id="R11+oe" />
              </p>
            )}
            {filterState.evaluationResult.type === "COMPARISON" && (
              <p className="flex font-bold text-indigo-600 dark:text-lime-400">
                {filterState.evaluationResult.operator === ">" && (
                  <FormattedMessage defaultMessage="Maior que" id="cqW1Ui" />
                )}
                {filterState.evaluationResult.operator === "<" && (
                  <FormattedMessage defaultMessage="Menor que" id="w8IRx3" />
                )}
                {filterState.evaluationResult.operator === "=" && (
                  <FormattedMessage defaultMessage="Igual a" id="OEvb/t" />
                )}
                {filterState.evaluationResult.operator === "!=" && (
                  <FormattedMessage defaultMessage="Diferente de" id="/xCK/b" />
                )}
                {filterState.evaluationResult.operator === ">=" && (
                  <FormattedMessage defaultMessage="Maior ou igual" id="rQTqbf" />
                )}
                {filterState.evaluationResult.operator === "<=" && (
                  <FormattedMessage defaultMessage="Menor ou igual" id="OMyoiH" />
                )}
              </p>
            )}

            {filterState.evaluationResult.type === "VALUE" && (
              <>
                <p className="flex font-bold text-indigo-600 dark:text-lime-400">
                  <FormattedMessage defaultMessage="Igual a" id="OEvb/t" />
                </p>

                <h1 className="text-xl font-bold">
                  <FormattedNumber
                    value={filterState.evaluationResult.value}
                    notation="standard"
                    style={isPercentStyle(filterState) ? "percent" : "decimal"}
                    maximumSignificantDigits={4}
                    maximumFractionDigits={2}
                  />
                </h1>
              </>
            )}

            {filterState.evaluationResult.type === "RANGE" && (
              <h1 className="flex items-center gap-x-1 text-xl font-bold">
                <span>
                  <FormattedNumber
                    value={filterState.evaluationResult.range[0]}
                    notation="compact"
                    style={isPercentStyle(filterState) ? "percent" : "decimal"}
                    maximumSignificantDigits={4}
                    maximumFractionDigits={2}
                  />
                </span>
                <span className="text-slate-500">...</span>
                <span>
                  <FormattedNumber
                    value={filterState.evaluationResult.range[1]}
                    notation="compact"
                    style={isPercentStyle(filterState) ? "percent" : "decimal"}
                    maximumSignificantDigits={4}
                    maximumFractionDigits={2}
                  />
                </span>
              </h1>
            )}

            {filterState.evaluationResult.type === "COMPARISON" && (
              <h1 className="flex items-center gap-x-1 text-xl font-bold">
                <FormattedNumber
                  value={filterState.evaluationResult.value}
                  notation="compact"
                  style={isPercentStyle(filterState) ? "percent" : "decimal"}
                  maximumSignificantDigits={4}
                  maximumFractionDigits={2}
                />
              </h1>
            )}
          </div>
        )}

        {/* Examples */}
        {!filterState.evaluationResult &&
          indicator.__typename !== "ScreenerServerSideIndicator" &&
          shouldShowExamples(indicator.kind) && (
            <div className="px-0.5">
              <div
                className={classNames(
                  "-mb-1 grid gap-x-4 rounded-xl rounded-b-2xl bg-slate-50 p-2 px-3 text-sm dark:bg-slate-900",
                  "[&>strong]:font-mono45 [&>strong]:font-bold [&>strong]:text-black dark:[&>strong]:text-yellow-300",
                  "[&>span]:font-medium [&>span]:text-slate-600 dark:[&>span]:font-bold dark:[&>span]:text-gray-300/80",
                )}
                style={{ gridTemplateColumns: "max-content 1fr" }}
              >
                {[IIndicatorKind.Financial].includes(indicator.kind) && (
                  <>
                    <strong>1m..10b</strong>
                    <span>
                      <FormattedMessage defaultMessage="Entre 1m e 10 bilhões" id="y2NMcm" />
                    </span>

                    <strong>{`>1m`}</strong>
                    <span>
                      <FormattedMessage defaultMessage="Maior que 1 milhão" id="+BJ2CA" />
                    </span>

                    <strong>{`<>1m`}</strong>
                    <span>
                      <FormattedMessage defaultMessage="Diferente de 1 milhão" id="ASj1pn" />
                    </span>

                    <strong>=1k</strong>
                    <span>
                      <FormattedMessage defaultMessage="Igual a 1000" id="bS5eaF" />
                    </span>
                  </>
                )}

                {[IIndicatorKind.Metric, IIndicatorKind.Number, IIndicatorKind.Ratio].includes(indicator.kind) && (
                  <>
                    <strong>1..10</strong>
                    <span>
                      <FormattedMessage defaultMessage="Entre 1 e 10" id="Vv6hkC" />
                    </span>

                    <strong>{`>20`}</strong>
                    <span>
                      <FormattedMessage defaultMessage="Maior que 20" id="2Ym4sX" />
                    </span>

                    <strong>{`<>30`}</strong>
                    <span>
                      <FormattedMessage defaultMessage="Diferente de 30" id="KLmUKV" />
                    </span>

                    <strong>=40</strong>
                    <span>
                      <FormattedMessage defaultMessage="Igual a 40" id="XkyrGq" />
                    </span>
                  </>
                )}

                {[IIndicatorKind.Percent].includes(indicator.kind) && (
                  <>
                    <strong>1%..15%</strong>
                    <span>
                      <FormattedMessage defaultMessage="Entre 1 e 15%" id="EKjksQ" />
                    </span>

                    <strong>{`>10%`}</strong>
                    <span>
                      <FormattedMessage defaultMessage="Maior que 10%" id="erKFAM" />
                    </span>

                    <strong>{`<>10%`}</strong>
                    <span>
                      <FormattedMessage defaultMessage="Diferente de 10%" id="VzmyL8" />
                    </span>
                    <strong>=10%</strong>
                    <span>
                      <FormattedMessage defaultMessage="Igual a 10%" id="M4rZmO" />
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
      </PopoverContent>
    </Popover>
  );
}

function shouldShowExamples(kind: IIndicatorKind) {
  return [
    IIndicatorKind.Financial,
    IIndicatorKind.Metric,
    IIndicatorKind.Number,
    IIndicatorKind.Ratio,
    IIndicatorKind.Percent,
  ].includes(kind);
}

function isPercentStyle(state: InternalFilterState) {
  if (state.evaluationResult?.type === "RANGE") {
    return (
      state.expression.includes("%") && state.evaluationResult.range[0] <= 1 && state.evaluationResult.range[1] <= 1
    );
  }
  if (state.evaluationResult?.type === "COMPARISON") {
    return state.expression.includes("%") && state.evaluationResult.value <= 1;
  }
  if (state.evaluationResult?.type === "VALUE") {
    return state.expression.includes("%") && state.evaluationResult.value <= 1;
  }
  return false;
}
